import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { formatMoney } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { TrendingUp, TrendingDown, Minus, ArrowRightLeft, RotateCcw, Loader2 } from "lucide-react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fxService } from "../services/dashboardService";
import { toast } from "sonner";
import { tx } from "../i18n";

/**
 * Reports the FX drift block from /api/report.
 * Shows per-currency breakdown of forward-looking (pending) and realized drift.
 * Hidden when there are no foreign-currency items in the period.
 */
export function FxDriftCard({ fxDrift, baseCurrency }) {
  const { t, lang } = useLang();
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const qc = useQueryClient();
  if (!fxDrift) return null;

  const totals = fxDrift.totals || {};
  const hasDrift = !!fxDrift.has_drift;

  const doResnapshot = async () => {
    setResetting(true);
    try {
      const res = await fxService.resnapshot("all");
      toast.success(`${t("fx_resnapshot_done")} (${res.total})`);
      qc.invalidateQueries();
      setConfirmReset(false);
    } catch (e) {
      console.error(e);
      toast.error("FX resnapshot failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-700" data-testid="fx-drift-card">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center">
            <ArrowRightLeft className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-slate-900 dark:text-slate-100">{t("fx_drift_title")}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl">{t("fx_drift_sub")}</p>
          </div>
        </div>
        {hasDrift && (
          <DriftTone
            value={totals.net_drift || 0}
            currency={baseCurrency}
            label={t("fx_drift_net")}
            big
          />
        )}
      </div>

      {!hasDrift ? (
        <div
          className="mt-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-600 dark:text-slate-300 flex items-start justify-between flex-wrap gap-3"
          data-testid="fx-drift-empty"
        >
          <span className="max-w-md">{t("fx_drift_empty")}</span>
          <Button
            data-testid="fx-resnapshot-btn"
            variant="outline"
            size="sm"
            onClick={() => setConfirmReset(true)}
            disabled={resetting}
            className="gap-2 shrink-0"
            title={t("fx_resnapshot_sub")}
          >
            {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {t("fx_resnapshot_btn")}
          </Button>
        </div>
      ) : (
        <>
          {/* Top-level summary */}
          <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="fx-drift-summary">
            <SummaryTile
              label={t("fx_drift_assets")}
              value={totals.assets_drift || 0}
              currency={baseCurrency}
              testid="fx-drift-assets-total"
              hint={tx(lang, "Positive = good for you", "Te beneficia si es positivo", "Te beneficia si es positivo")}
              tone="asset"
            />
            <SummaryTile
              label={t("fx_drift_liabilities")}
              value={totals.liabilities_drift || 0}
              currency={baseCurrency}
              testid="fx-drift-liabilities-total"
              hint={tx(lang, "Positive = costs you more", "Te cuesta más si es positivo", "Te cuesta más si es positivo")}
              tone="liability"
            />
            <SummaryTile
              label={t("fx_drift_realized")}
              value={totals.realized_drift || 0}
              currency={baseCurrency}
              testid="fx-drift-realized-total"
              hint={tx(lang, "Vs. today's rate", "Comparado con la tasa de hoy", "Comparado con la tasa de hoy")}
              tone="neutral"
            />
          </div>

          {/* Per-currency rows */}
          <div className="mt-4 grid gap-3" data-testid="fx-drift-by-currency">
            {fxDrift.by_currency.map((row) => (
              <CurrencyRow key={row.currency} row={row} baseCurrency={baseCurrency} />
            ))}
          </div>

          {/* Reset baseline footer */}
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-start justify-between flex-wrap gap-3">
            <div className="text-xs text-slate-500 dark:text-slate-400 max-w-md">{t("fx_resnapshot_sub")}</div>
            <Button
              data-testid="fx-resnapshot-btn"
              variant="outline"
              size="sm"
              onClick={() => setConfirmReset(true)}
              disabled={resetting}
              className="gap-2"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              {t("fx_resnapshot_btn")}
            </Button>
          </div>
        </>
      )}
      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent data-testid="fx-resnapshot-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("fx_resnapshot_btn")}</AlertDialogTitle>
            <AlertDialogDescription>{t("fx_resnapshot_confirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="fx-resnapshot-cancel">
              {tx(lang, "Cancel", "Cancelar", "Cancelar")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="fx-resnapshot-confirm"
              onClick={doResnapshot}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {tx(lang, "Continue", "Continuar", "Continuar")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function CurrencyRow({ row, baseCurrency }) {
  const { t } = useLang();
  const { sections } = row;
  return (
    <div
      className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
      data-testid={`fx-drift-row-${row.currency}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            {row.currency} → {baseCurrency}
          </Badge>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {row.items_count} {row.items_count === 1 ? "ítem" : "ítems"}
          </span>
        </div>
        <DriftTone
          value={row.net_drift}
          currency={baseCurrency}
          label={t("fx_drift_net")}
          testid={`fx-drift-row-${row.currency}-net`}
        />
      </div>
      <div className="grid sm:grid-cols-3 gap-2">
        <SectionTile
          label={t("fx_drift_pending_receivables")}
          bucket={sections.pending_receivables}
          currency={baseCurrency}
          tone="asset"
          testid={`fx-drift-${row.currency}-receivables`}
        />
        <SectionTile
          label={t("fx_drift_pending_payments")}
          bucket={sections.pending_payments}
          currency={baseCurrency}
          tone="liability"
          testid={`fx-drift-${row.currency}-payments`}
        />
        <SectionTile
          label={t("fx_drift_realized_section")}
          bucket={sections.realized}
          currency={baseCurrency}
          tone="neutral"
          testid={`fx-drift-${row.currency}-realized`}
        />
      </div>
    </div>
  );
}

function SectionTile({ label, bucket, currency, tone, testid }) {
  const { t } = useLang();
  if (!bucket) {
    return (
      <div
        className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-800 text-xs text-slate-400"
        data-testid={`${testid}-empty`}
      >
        <div className="font-semibold text-slate-500 dark:text-slate-400 mb-1">{label}</div>
        —
      </div>
    );
  }
  // tone "liability": +drift is bad
  const sign = tone === "liability" ? -1 : 1;
  const adjustedTone = (bucket.drift || 0) * sign;
  return (
    <div
      className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-800"
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 tabular-nums">
        {t("fx_drift_recorded")}: {formatMoney(bucket.recorded_total, currency)}
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
        {t("fx_drift_current")}: {formatMoney(bucket.current_total, currency)}
      </div>
      <div className="mt-1.5">
        <DriftTone value={adjustedTone} currency={currency} small />
        <span className="text-[10px] text-slate-400 ml-1.5 tabular-nums">
          ({bucket.drift_pct >= 0 ? "+" : ""}{bucket.drift_pct}%)
        </span>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, currency, hint, tone, testid }) {
  const sign = tone === "liability" ? -1 : 1;
  return (
    <div
      className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-800"
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{label}</div>
      <DriftTone value={value * sign} currency={currency} />
      <div className="text-[11px] text-slate-400 mt-1">{hint}</div>
    </div>
  );
}

function DriftTone({ value, currency, label, big, small, testid }) {
  const v = Number(value) || 0;
  const isPos = v > 0.005;
  const isNeg = v < -0.005;
  const color = isPos ? "text-emerald-700" : isNeg ? "text-red-600" : "text-slate-600 dark:text-slate-300";
  const Icon = isPos ? TrendingUp : isNeg ? TrendingDown : Minus;
  const size = big ? "text-2xl" : small ? "text-sm" : "text-lg";
  return (
    <div
      className={`flex items-center gap-1.5 ${color}`}
      data-testid={testid}
    >
      <Icon className={big ? "h-5 w-5" : "h-3.5 w-3.5"} />
      <span className={`font-display font-bold tabular-nums ${size}`}>
        {isPos ? "+" : ""}{formatMoney(v, currency)}
      </span>
      {label && <span className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold ml-1">{label}</span>}
    </div>
  );
}
